(() => {
  'use strict';

  /*
   * ============================================================
   * MASTERFORCE TCG
   * Search Product Card Controls
   * ============================================================
   *
   * Funciones:
   * - Incrementar cantidad
   * - Reducir cantidad
   * - Validar cantidad manual
   * - Respetar inventario máximo
   * - Agregar variantes al carrito mediante Shopify AJAX API
   * - Compatible con resultados reemplazados por filtros AJAX
   * - Evita listeners duplicados en Theme Editor
   *
   * Scope:
   * .main-search
   * ============================================================
   */

  const GLOBAL_FLAG = '__MF_SEARCH_CARD_CONTROLS_INITIALIZED__';

  if (window[GLOBAL_FLAG]) {
    return;
  }

  window[GLOBAL_FLAG] = true;

  const SEARCH_ROOT_SELECTOR = '.main-search';

  const SELECTORS = {
    buybox: '[data-mf-card-buybox]',
    minus: '[data-mf-qty-minus]',
    plus: '[data-mf-qty-plus]',
    input: '[data-mf-qty-input]',
    addToCart: '[data-mf-card-add-to-cart]'
  };

  const CLASSES = {
    loading: 'is-loading',
    success: 'is-added',
    error: 'has-error'
  };

  const SHOPIFY_ROOT =
    window.Shopify &&
    window.Shopify.routes &&
    window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

  const CART_ADD_URL = `${SHOPIFY_ROOT}cart/add.js`;
  const CART_URL = `${SHOPIFY_ROOT}cart.js`;

  /*
   * ============================================================
   * HELPERS
   * ============================================================
   */

  function isInsideSearch(element) {
    return Boolean(element && element.closest(SEARCH_ROOT_SELECTOR));
  }

  function getBuybox(element) {
    if (!element) return null;

    return element.closest(SELECTORS.buybox);
  }

  function getQuantityInput(buybox) {
    if (!buybox) return null;

    return buybox.querySelector(SELECTORS.input);
  }

  function parseInteger(value, fallback = 1) {
    const parsed = Number.parseInt(value, 10);

    if (!Number.isFinite(parsed)) {
      return fallback;
    }

    return parsed;
  }

  function getMinimum(input) {
    if (!input) return 1;

    const minimum = parseInteger(input.getAttribute('min'), 1);

    return Math.max(1, minimum);
  }

  function getMaximum(buybox, input) {
    let maximum = 99;

    if (buybox) {
      maximum = parseInteger(
        buybox.getAttribute('data-quantity-max'),
        maximum
      );
    }

    if (input && input.getAttribute('max')) {
      const inputMaximum = parseInteger(
        input.getAttribute('max'),
        maximum
      );

      maximum = Math.min(maximum, inputMaximum);
    }

    return Math.max(1, maximum);
  }

  function clampQuantity(value, minimum, maximum) {
    let quantity = parseInteger(value, minimum);

    if (quantity < minimum) {
      quantity = minimum;
    }

    if (quantity > maximum) {
      quantity = maximum;
    }

    return quantity;
  }

  /*
   * ============================================================
   * QUANTITY STATE
   * ============================================================
   */

  function syncQuantityState(buybox) {
    if (!buybox) return 1;

    const input = getQuantityInput(buybox);

    if (!input) return 1;

    const minimum = getMinimum(input);
    const maximum = getMaximum(buybox, input);

    const quantity = clampQuantity(
      input.value,
      minimum,
      maximum
    );

    input.value = String(quantity);

    const minusButton = buybox.querySelector(SELECTORS.minus);
    const plusButton = buybox.querySelector(SELECTORS.plus);

    if (minusButton) {
      minusButton.disabled = quantity <= minimum;
      minusButton.setAttribute(
        'aria-disabled',
        quantity <= minimum ? 'true' : 'false'
      );
    }

    if (plusButton) {
      plusButton.disabled = quantity >= maximum;
      plusButton.setAttribute(
        'aria-disabled',
        quantity >= maximum ? 'true' : 'false'
      );
    }

    return quantity;
  }

  function changeQuantity(button, direction) {
    const buybox = getBuybox(button);

    if (!buybox) return;

    const input = getQuantityInput(buybox);

    if (!input) return;

    const minimum = getMinimum(input);
    const maximum = getMaximum(buybox, input);

    const currentValue = clampQuantity(
      input.value,
      minimum,
      maximum
    );

    const newValue = clampQuantity(
      currentValue + direction,
      minimum,
      maximum
    );

    input.value = String(newValue);

    syncQuantityState(buybox);

    input.dispatchEvent(
      new Event('change', {
        bubbles: true
      })
    );
  }

  /*
   * ============================================================
   * BUTTON STATE
   * ============================================================
   */

  function setButtonLoading(button, loading) {
    if (!button) return;

    if (loading) {
      button.disabled = true;
      button.classList.add(CLASSES.loading);
      button.setAttribute('aria-busy', 'true');
    } else {
      button.disabled = false;
      button.classList.remove(CLASSES.loading);
      button.removeAttribute('aria-busy');
    }
  }

  function clearButtonStates(button) {
    if (!button) return;

    button.classList.remove(
      CLASSES.success,
      CLASSES.error
    );
  }

  function showTemporarySuccess(button) {
    if (!button) return;

    clearButtonStates(button);

    button.classList.add(CLASSES.success);

    const originalLabel =
      button.getAttribute('aria-label') || 'Agregar al carrito';

    button.dataset.mfOriginalAriaLabel = originalLabel;

    button.setAttribute(
      'aria-label',
      'Producto agregado al carrito'
    );

    window.setTimeout(() => {
      button.classList.remove(CLASSES.success);

      if (button.dataset.mfOriginalAriaLabel) {
        button.setAttribute(
          'aria-label',
          button.dataset.mfOriginalAriaLabel
        );

        delete button.dataset.mfOriginalAriaLabel;
      }
    }, 1600);
  }

  function showTemporaryError(button, message) {
    if (!button) return;

    clearButtonStates(button);

    button.classList.add(CLASSES.error);

    const originalLabel =
      button.getAttribute('aria-label') || 'Agregar al carrito';

    button.dataset.mfOriginalAriaLabel = originalLabel;

    button.setAttribute(
      'aria-label',
      message || 'No se pudo agregar al carrito'
    );

    window.setTimeout(() => {
      button.classList.remove(CLASSES.error);

      if (button.dataset.mfOriginalAriaLabel) {
        button.setAttribute(
          'aria-label',
          button.dataset.mfOriginalAriaLabel
        );

        delete button.dataset.mfOriginalAriaLabel;
      }
    }, 2200);
  }

  /*
   * ============================================================
   * SHOPIFY CART
   * ============================================================
   */

  async function addItemToCart(variantId, quantity) {
    const response = await fetch(CART_ADD_URL, {
      method: 'POST',

      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },

      credentials: 'same-origin',

      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity
          }
        ]
      })
    });

    let payload = null;

    try {
      payload = await response.json();
    } catch (error) {
      payload = null;
    }

    if (!response.ok) {
      let message = 'No se pudo agregar el producto al carrito.';

      if (payload) {
        if (payload.description) {
          message = payload.description;
        } else if (payload.message) {
          message = payload.message;
        }
      }

      throw new Error(message);
    }

    return payload;
  }

  async function getCart() {
    const response = await fetch(CART_URL, {
      method: 'GET',

      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },

      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error('No se pudo actualizar el carrito.');
    }

    return response.json();
  }

  /*
   * ============================================================
   * CART EVENTS
   * ============================================================
   */

  function publishCartEvents(cart, addedItem) {
    const detail = {
      source: 'masterforce-search',
      cart,
      addedItem
    };

    /*
     * Evento MasterForce
     */
    document.dispatchEvent(
      new CustomEvent('mf:cart-updated', {
        detail
      })
    );

    window.dispatchEvent(
      new CustomEvent('mf:cart-updated', {
        detail
      })
    );

    /*
     * Eventos genéricos compatibles con integraciones
     */
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail
      })
    );

    document.dispatchEvent(
      new CustomEvent('cart:refresh', {
        detail
      })
    );

    /*
     * Compatibilidad con temas Shopify que utilizan Pub/Sub
     */
    if (
      typeof window.publish === 'function' &&
      window.PUB_SUB_EVENTS &&
      window.PUB_SUB_EVENTS.cartUpdate
    ) {
      try {
        window.publish(
          window.PUB_SUB_EVENTS.cartUpdate,
          {
            source: 'masterforce-search',
            cartData: cart,
            productVariantId:
              addedItem &&
              addedItem.items &&
              addedItem.items[0]
                ? addedItem.items[0].variant_id
                : null
          }
        );
      } catch (error) {
        console.warn(
          '[MasterForce Search] No fue posible publicar cartUpdate:',
          error
        );
      }
    }
  }

  /*
   * ============================================================
   * COMMON CART COUNT FALLBACK
   * ============================================================
   *
   * Solo actualiza elementos expresamente identificados
   * como contadores. No destruye markup del header.
   * ============================================================
   */

  function updateKnownCartCounters(itemCount) {
    const selectors = [
      '[data-cart-count]',
      '[data-cart-count-number]',
      '[data-header-cart-count]',
      '[data-mf-cart-count]'
    ];

    selectors.forEach((selector) => {
      document
        .querySelectorAll(selector)
        .forEach((element) => {
          element.textContent = String(itemCount);

          if (itemCount > 0) {
            element.removeAttribute('hidden');
          }
        });
    });
  }

  /*
   * ============================================================
   * ADD BUTTON
   * ============================================================
   */

  async function handleAddToCart(button) {
    if (!button || button.disabled) return;

    const buybox = getBuybox(button);

    if (!buybox) return;

    const input = getQuantityInput(buybox);

    if (!input) return;

    const variantId = parseInteger(
      button.getAttribute('data-variant-id'),
      0
    );

    if (!variantId) {
      console.error(
        '[MasterForce Search] Variant ID no válido.'
      );

      showTemporaryError(
        button,
        'Variante no válida'
      );

      return;
    }

    const quantity = syncQuantityState(buybox);

    clearButtonStates(button);
    setButtonLoading(button, true);

    try {
      const addedItem = await addItemToCart(
        variantId,
        quantity
      );

      /*
       * Obtenemos el carrito real después del POST.
       * Esto también permite sincronizar el contador.
       */
      const cart = await getCart();

      updateKnownCartCounters(cart.item_count);

      publishCartEvents(cart, addedItem);

      /*
       * Reiniciamos el selector en 1 después de una
       * adición exitosa.
       */
      input.value = '1';

      syncQuantityState(buybox);

      showTemporarySuccess(button);
    } catch (error) {
      console.error(
        '[MasterForce Search] Error al agregar al carrito:',
        error
      );

      showTemporaryError(
        button,
        error && error.message
          ? error.message
          : 'No se pudo agregar al carrito'
      );
    } finally {
      setButtonLoading(button, false);
    }
  }

  /*
   * ============================================================
   * CLICK DELEGATION
   * ============================================================
   *
   * No enlazamos cada tarjeta individualmente.
   *
   * Esto es intencional:
   * Search & Discovery / collection-filter.js puede reemplazar
   * resultados sin recargar la página.
   *
   * La delegación mantiene los controles funcionando.
   * ============================================================
   */

  function handleDocumentClick(event) {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const minusButton = target.closest(SELECTORS.minus);

    if (minusButton && isInsideSearch(minusButton)) {
      event.preventDefault();

      changeQuantity(minusButton, -1);

      return;
    }

    const plusButton = target.closest(SELECTORS.plus);

    if (plusButton && isInsideSearch(plusButton)) {
      event.preventDefault();

      changeQuantity(plusButton, 1);

      return;
    }

    const addButton = target.closest(
      SELECTORS.addToCart
    );

    if (addButton && isInsideSearch(addButton)) {
      event.preventDefault();

      handleAddToCart(addButton);
    }
  }

  /*
   * ============================================================
   * MANUAL INPUT VALIDATION
   * ============================================================
   */

  function handleQuantityInput(event) {
    const input = event.target;

    if (
      !(input instanceof HTMLInputElement) ||
      !input.matches(SELECTORS.input) ||
      !isInsideSearch(input)
    ) {
      return;
    }

    const buybox = getBuybox(input);

    if (!buybox) return;

    /*
     * Durante escritura permitimos temporalmente vacío.
     */
    if (
      event.type === 'input' &&
      input.value === ''
    ) {
      return;
    }

    syncQuantityState(buybox);
  }

  /*
   * ============================================================
   * KEYBOARD
   * ============================================================
   */

  function handleQuantityKeydown(event) {
    const input = event.target;

    if (
      !(input instanceof HTMLInputElement) ||
      !input.matches(SELECTORS.input) ||
      !isInsideSearch(input)
    ) {
      return;
    }

    const buybox = getBuybox(input);

    if (!buybox) return;

    if (event.key === 'ArrowUp') {
      event.preventDefault();

      const plus = buybox.querySelector(
        SELECTORS.plus
      );

      if (plus) {
        changeQuantity(plus, 1);
      }
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();

      const minus = buybox.querySelector(
        SELECTORS.minus
      );

      if (minus) {
        changeQuantity(minus, -1);
      }
    }
  }

  /*
   * ============================================================
   * INITIAL STATE
   * ============================================================
   */

  function initializeExistingBuyboxes() {
    document
      .querySelectorAll(
        `${SEARCH_ROOT_SELECTOR} ${SELECTORS.buybox}`
      )
      .forEach((buybox) => {
        syncQuantityState(buybox);
      });
  }

  /*
   * ============================================================
   * EVENTS
   * ============================================================
   */

  document.addEventListener(
    'click',
    handleDocumentClick
  );

  document.addEventListener(
    'input',
    handleQuantityInput
  );

  document.addEventListener(
    'change',
    handleQuantityInput
  );

  document.addEventListener(
    'keydown',
    handleQuantityKeydown
  );

  /*
   * Inicialización normal
   */
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeExistingBuyboxes,
      {
        once: true
      }
    );
  } else {
    initializeExistingBuyboxes();
  }

  /*
   * Theme Editor
   */
  document.addEventListener(
    'shopify:section:load',
    initializeExistingBuyboxes
  );
})();